import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Pencil, Trash2, Users } from 'lucide-react';
import axios from '../../interceptors/axios';
import { url } from '../../utils/confserveur';
import { useEquipesQCM } from '../../hooks/fetch_api_hook/useEquipesQCM';
import EquipeForm from './EquipeForm';

const ModalEquipesQCM = ({ session, cartes, eleves, onClose }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: equipes, isLoading } = useEquipesQCM(session.id, refreshKey);

  const [editingEquipe, setEditingEquipe] = useState(null); // equipe object ou null
  const [showAddForm, setShowAddForm] = useState(false);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Élèves déjà assignés (sauf ceux de l'équipe en cours d'édition)
  const elevesDisponibles = useMemo(() => {
    const ids = new Set();
    (equipes || []).forEach((eq) => {
      if (editingEquipe && eq.id === editingEquipe.id) return;
      eq.eleves.forEach((id) => ids.add(id));
    });
    return eleves.filter((e) => !ids.has(e.id));
  }, [equipes, eleves, editingEquipe]);

  // Cartes déjà assignées (sauf celle de l'équipe en cours d'édition)
  const cartesDisponibles = useMemo(() => {
    const ids = new Set();
    (equipes || []).forEach((eq) => {
      if (editingEquipe && eq.id === editingEquipe.id) return;
      if (eq.carte) ids.add(eq.carte);
    });
    return cartes.filter((c) => !ids.has(c.id));
  }, [equipes, cartes, editingEquipe]);

  // Map id → eleve pour afficher les noms
  const elevesMap = useMemo(() => {
    const m = new Map();
    eleves.forEach((e) => m.set(e.id, e));
    return m;
  }, [eleves]);

  // Map carte id → code_aruco
  const cartesMap = useMemo(() => {
    const m = new Map();
    cartes.forEach((c) => m.set(c.id, c));
    return m;
  }, [cartes]);

  const handleAdd = async (data) => {
    await axios.post(`${url}equipes-qcm/`, { ...data, session: session.id });
    setShowAddForm(false);
    refetch();
  };

  const handleUpdate = async (data) => {
    await axios.put(`${url}equipes-qcm/${editingEquipe.id}/`, { ...data, session: session.id });
    setEditingEquipe(null);
    refetch();
  };

  const handleDelete = async (equipe) => {
    await axios.delete(`${url}equipes-qcm/${equipe.id}/`);
    refetch();
  };

  const isFormOpen = showAddForm || editingEquipe;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={(e) => e.stopPropagation()} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Users size={20} />
              Équipes
            </h3>
            <p className="text-white/70 text-xs mt-0.5">{session.titre}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20 transition">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <p className="text-center text-gray-500 py-8">Chargement des équipes...</p>
          ) : (
            <>
              {/* Liste des équipes */}
              {(equipes || []).map((eq) => {
                if (editingEquipe?.id === eq.id) {
                  return (
                    <EquipeForm
                      key={eq.id}
                      cartes={cartesDisponibles}
                      eleves={elevesDisponibles}
                      equipe={eq}
                      onSave={handleUpdate}
                      onCancel={() => setEditingEquipe(null)}
                    />
                  );
                }
                const carte = cartesMap.get(eq.carte);
                return (
                  <div key={eq.id} className="p-3 rounded-xl border border-gray-200 bg-gray-50/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{eq.nom}</span>
                        {carte && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-100 text-indigo-600">
                            Carte #{carte.code_aruco}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setShowAddForm(false); setEditingEquipe(eq); }}
                          disabled={isFormOpen}
                          aria-label="Modifier"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(eq)}
                          disabled={isFormOpen}
                          aria-label="Supprimer"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {eq.eleves.map((eleveId) => {
                        const el = elevesMap.get(eleveId);
                        return el ? (
                          <span key={eleveId} className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs">
                            {el.nom} {el.prenom}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Formulaire ajout */}
              {showAddForm ? (
                <EquipeForm
                  cartes={cartesDisponibles}
                  eleves={elevesDisponibles}
                  onSave={handleAdd}
                  onCancel={() => setShowAddForm(false)}
                />
              ) : (
                !editingEquipe && elevesDisponibles.length > 0 && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition w-full justify-center"
                  >
                    <Plus size={16} />
                    Ajouter une équipe
                  </button>
                )
              )}

              {/* Tous les élèves assignés */}
              {!isFormOpen && elevesDisponibles.length === 0 && (equipes || []).length > 0 && (
                <p className="text-sm text-emerald-600 text-center py-2">
                  Tous les élèves sont répartis dans les équipes
                </p>
              )}

              {/* Aucune équipe */}
              {!isLoading && (equipes || []).length === 0 && !showAddForm && (
                <div className="text-center py-8">
                  <Users size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">Aucune équipe pour cette session</p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition mx-auto"
                  >
                    <Plus size={16} />
                    Créer la première équipe
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between bg-gray-50">
          <span className="text-xs text-gray-500">
            {(equipes || []).length} équipe{(equipes || []).length > 1 ? 's' : ''}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white transition"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalEquipesQCM;
